import Head from "next/head";
import Link from "next/link";
import React from "react";
import Navigation from "../../core-web/components/landing/molecules/Navigation";
import {PageHead} from "../../core-web/components/core/templates/PageHead";
import {WebsiteLayout} from "../../core-web/components/landing/WebsiteLayout";
import Footer from "../../core-web/components/landing/molecules/Footer";
import {getAllPostsMetadata, getPostBySlug, PostFrontmatter, PostMetadata} from "../../core-web/services/blog";
import {renderPost, TocItem} from "../../core-web/services/blogRender";
import {BlogCardPost, formatPostDate, getImageSrc, PostCard} from "../../core-web/components/blog/PostCard";
import {PostToc, PostTocBox} from "../../core-web/components/blog/PostToc";

const SITE_URL = "https://www.notionsender.com";
// Fewer sections than this and a navigator is just noise
const MIN_SECTIONS_FOR_TOC = 3;
const MORE_ARTICLES_COUNT = 3;

type PostPageProps = {
  slug: string
  frontmatter: PostFrontmatter
  html: string
  toc: TocItem[]
  readingMinutes: number
  posts: BlogCardPost[]
}

// "Updated" is only meaningful when it is later than the publish date (compared by calendar day)
const showUpdated = ({date, updated}: PostFrontmatter) =>
  Boolean(updated) && (!date || updated!.slice(0, 10) > date.slice(0, 10));

const absoluteImageUrl = (socialImage: string) =>
  socialImage.startsWith("http://") || socialImage.startsWith("https://")
    ? socialImage
    : `${SITE_URL}${getImageSrc(socialImage)}`;

const articleJsonLd = (slug: string, frontmatter: PostFrontmatter) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: frontmatter.metaTitle || frontmatter.title,
  description: frontmatter.metaDesc,
  mainEntityOfPage: `${SITE_URL}/blog/${slug}`,
  ...(frontmatter.socialImage ? {image: absoluteImageUrl(frontmatter.socialImage)} : {}),
  ...(frontmatter.date ? {datePublished: frontmatter.date} : {}),
  ...(frontmatter.date || frontmatter.updated
    ? {dateModified: showUpdated(frontmatter) ? frontmatter.updated : frontmatter.date}
    : {}),
  publisher: {"@type": "Organization", name: "NotionSender", url: SITE_URL},
});

export default function PostPage({slug, frontmatter, html, toc, readingMinutes, posts}: PostPageProps) {
  const hasToc = toc.length >= MIN_SECTIONS_FOR_TOC;

  return <PageHead
    title={`${frontmatter.metaTitle} | NotionSender`}
    description={frontmatter.metaDesc}
    canonicalUrl={`${SITE_URL}/blog/${slug}`}>
    <Head>
      <script
        key="article-jsonld"
        type="application/ld+json"
        // "<" is escaped so post text can never close the script tag
        dangerouslySetInnerHTML={{__html: JSON.stringify(articleJsonLd(slug, frontmatter)).replace(/</g, "\\u003c")}}
      />
    </Head>
    <WebsiteLayout>
      <Navigation/>

      <div
        className={[
          "mx-auto mt-16 mb-8 max-w-[1100px] px-2",
          hasToc
            ? "lg:grid lg:grid-cols-[minmax(0,720px)_252px] lg:justify-center lg:gap-14"
            : "lg:max-w-[720px]",
        ].join(" ")}
      >
        <article className="min-w-0">
          <Link
            href="/blog"
            className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition hover:text-blue-500"
          >
            <span aria-hidden="true">←</span> All articles
          </Link>

          <h1 className="text-3xl font-bold leading-tight text-gray-900 md:text-4xl">{frontmatter.title}</h1>

          <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
            {frontmatter.date && <span>Published <strong className="font-semibold text-gray-700">{formatPostDate(frontmatter.date)}</strong></span>}
            {showUpdated(frontmatter) && <>
              <span aria-hidden="true">·</span>
              <span>Updated <strong className="font-semibold text-gray-700">{formatPostDate(frontmatter.updated)}</strong></span>
            </>}
            {frontmatter.date && <span aria-hidden="true">·</span>}
            <span>{readingMinutes} min read</span>
          </p>

          {hasToc && <PostTocBox items={toc}/>}

          {frontmatter.socialImage && (
            <img
              alt={frontmatter.title}
              src={getImageSrc(frontmatter.socialImage)}
              className="mt-8 h-auto w-full rounded-2xl"
            />
          )}

          <div
            className="prose prose-lg mt-8 max-w-none prose-headings:scroll-mt-28"
            dangerouslySetInnerHTML={{__html: html}}
          />
        </article>

        {hasToc && (
          <aside className="hidden lg:block">
            <PostToc items={toc}/>
          </aside>
        )}
      </div>

      {!frontmatter.disableMore && posts.length > 0 && (
        <section aria-label="More articles" className="mx-auto mb-16 mt-20 max-w-[1100px] px-2">
          <h2 className="mb-6 text-xl font-medium">More articles</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {posts.map(post => <PostCard key={post.slug} post={post}/>)}
          </div>
        </section>
      )}

      <Footer/>
    </WebsiteLayout>
  </PageHead>
}

const toCard = ({slug, frontmatter}: PostMetadata): BlogCardPost => ({
  slug,
  title: frontmatter.title,
  metaDesc: frontmatter.metaDesc,
  socialImage: frontmatter.socialImage,
  date: frontmatter.date,
});

export async function getStaticPaths() {
  return {
    paths: getAllPostsMetadata().map(({slug}) => ({params: {slug}})),
    fallback: false,
  };
}

export async function getStaticProps({params: {slug}}: {params: {slug: string}}) {
  const blogPost = getPostBySlug(slug);

  if (!blogPost) {
    return {notFound: true};
  }

  const {html, toc, readingMinutes} = renderPost(blogPost.content);

  // Only the "More articles" cards are rendered, so ship just those.
  const posts = getAllPostsMetadata()
    .filter(post => post.slug !== slug)
    .slice(0, MORE_ARTICLES_COUNT)
    .map(toCard);

  return {
    props: {
      slug: blogPost.slug,
      frontmatter: blogPost.frontmatter,
      html,
      toc,
      readingMinutes,
      posts,
    },
  };
}
